<template>
  <div class="container">
    <a-card title="全院分包付款统计">
      <div class="header">
        <a-space>
          <a-select
            v-model="selectedMonth"
            style="width: 120px"
            @change="onMonthChange"
          >
            <a-select-option v-for="month in monthOptions" :key="month" :value="month">
              {{ month }}
            </a-select-option>
          </a-select>
        </a-space>
        <a-button type="link" @click="goBack">返回</a-button>
      </div>

      <Padding />

      <!-- 总院设计项目分包付款计划 -->
      <div>
        <h3>总院设计项目分包付款计划</h3>
        <a-table
          bordered
          :data-source="designList"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 'max-content' }"
        >
          <a-table-column
            title="部门"
            data-index="deptName"
            width="150px"
            :customHeaderCell="() => ({ rowSpan: 2 })"
          />
          <a-table-column
            title="累计回款额"
            data-index="totalCollectionAmount"
            width="120px"
            align="right"
            :customHeaderCell="() => ({ rowSpan: 2 })"
          />
          <a-table-column-group :title="`${currentMonth}月累计回款额`">
            <a-table-column
              v-for="week in weekColumns"
              :key="'design-collection-week-' + week.weekNumber"
              :title="`第${week.weekNumber}周`"
              width="100px"
              align="right"
            >
              <template #default="text, record">
                <span>{{ record[`week${week.weekNumber}Collection`] || 0 }}</span>
              </template>
            </a-table-column>
          </a-table-column-group>
          <a-table-column-group title="分包付款">
            <a-table-column title="需支付分包金额" width="120px" align="right">
              <template #default="text, record">
                <span>{{ record.totalPayableAmount || 0 }}</span>
              </template>
            </a-table-column>
            <a-table-column title="已支付分包金额" width="120px" align="right">
              <template #default="text, record">
                <span>{{ record.totalPaidAmount || 0 }}</span>
              </template>
            </a-table-column>
            <a-table-column title="剩余应支付分包金额" width="150px" align="right">
              <template #default="text, record">
                <span :style="{ color: (record.totalRemainingAmount || 0) > 0 ? '#f5222d' : '#52c41a' }">
                  {{ record.totalRemainingAmount || 0 }}
                </span>
              </template>
            </a-table-column>
            <a-table-column title="支付比例" width="100px" align="right">
              <template #default="text, record">
                <span>{{ getPaymentRatio(record) }}</span>
              </template>
            </a-table-column>
          </a-table-column-group>
        </a-table>
      </div>

      <Padding />

      <!-- EPC项目分包付款计划 -->
      <div>
        <h3>EPC项目分包付款计划</h3>
        <a-table
          bordered
          :data-source="epcList"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 'max-content' }"
        >
          <a-table-column
            title="部门"
            data-index="deptName"
            width="150px"
            :customHeaderCell="() => ({ rowSpan: 2 })"
          />
          <a-table-column
            title="累计回款额"
            data-index="totalCollectionAmount"
            width="120px"
            align="right"
            :customHeaderCell="() => ({ rowSpan: 2 })"
          />
          <a-table-column-group :title="`${currentMonth}月累计回款额`">
            <a-table-column
              v-for="week in weekColumns"
              :key="'epc-collection-week-' + week.weekNumber"
              :title="`第${week.weekNumber}周`"
              width="100px"
              align="right"
            >
              <template #default="text, record">
                <span>{{ record[`week${week.weekNumber}Collection`] || 0 }}</span>
              </template>
            </a-table-column>
          </a-table-column-group>
          <a-table-column-group title="分包付款">
            <a-table-column title="需支付分包金额" width="120px" align="right">
              <template #default="text, record">
                <span>{{ record.totalPayableAmount || 0 }}</span>
              </template>
            </a-table-column>
            <a-table-column title="已支付分包金额" width="120px" align="right">
              <template #default="text, record">
                <span>{{ record.totalPaidAmount || 0 }}</span>
              </template>
            </a-table-column>
            <a-table-column title="剩余应支付分包金额" width="150px" align="right">
              <template #default="text, record">
                <span :style="{ color: (record.totalRemainingAmount || 0) > 0 ? '#f5222d' : '#52c41a' }">
                  {{ record.totalRemainingAmount || 0 }}
                </span>
              </template>
            </a-table-column>
            <a-table-column title="支付比例" width="100px" align="right">
              <template #default="text, record">
                <span>{{ getPaymentRatio(record) }}</span>
              </template>
            </a-table-column>
          </a-table-column-group>
        </a-table>
      </div>

      <!-- 空状态 -->
      <a-empty v-if="!loading && designList.length === 0 && epcList.length === 0" description="暂无数据" />
    </a-card>
  </div>
</template>

<script>
import { getWeeks, getMonthlyStatistics } from "@/api/finance/subpackage-payment-plan";
import moment from "moment";

export default {
  data() {
    return {
      selectedMonth: null,
      designList: [],
      epcList: [],
      loading: false,
      weekColumns: [],
      monthOptions: [],
    };
  },

  computed: {
    currentMonth() {
      if (this.selectedMonth) {
        return moment(this.selectedMonth).format("M");
      }
      return moment().format("M");
    },

    yearMonth() {
      if (this.selectedMonth) {
        return moment(this.selectedMonth).format("YYYY-MM");
      }
      return moment().format("YYYY-MM");
    },
  },

  created() {
    this.initMonthOptions();
    this.initFromQuery();
    this.getWeekColumns();
  },

  methods: {
    initMonthOptions() {
      const currentYear = moment().year();
      this.monthOptions = [];
      for (let i = 1; i <= 12; i++) {
        this.monthOptions.push(`${currentYear}-${String(i).padStart(2, '0')}`);
      }
    },

    initFromQuery() {
      const query = this.$route.query;
      if (query.yearMonth) {
        this.selectedMonth = query.yearMonth;
      } else {
        this.selectedMonth = moment().format("YYYY-MM");
      }
    },

    async getWeekColumns() {
      this.loading = true;
      try {
        const res = await getWeeks(this.yearMonth);
        if (res && Array.isArray(res)) {
          this.weekColumns = res.map((week) => ({
            weekNumber: week.weekNumber,
          }));
        }
        this.getSummaryData();
      } catch (err) {
        console.error("获取周信息失败", err);
        this.loading = false;
      }
    },

    async getSummaryData() {
      try {
        const [year, month] = this.yearMonth.split("-");
        const res = await getMonthlyStatistics({
          year: parseInt(year),
          month: parseInt(month),
        });
        if (res) {
          this.designList = res.designList || [];
          this.epcList = res.epcList || [];
        } else {
          this.designList = [];
          this.epcList = [];
        }
      } catch (err) {
        console.error("获取统计数据失败", err);
        this.designList = [];
        this.epcList = [];
      } finally {
        this.loading = false;
      }
    },

    getPaymentRatio(record) {
      if (record.totalPayableAmount && record.totalPayableAmount > 0) {
        const ratio = (record.totalPaidAmount / record.totalPayableAmount * 100).toFixed(2);
        return `${ratio}%`;
      }
      return "0%";
    },

    onMonthChange(value) {
      this.selectedMonth = value;
      this.getWeekColumns();
    },

    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.container {
  padding: 16px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
